import React, { useContext, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DestinationsPicker } from "../widgets";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { countries, TranslationContext } from "../../../umbilical_tools";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import { DatepickerField, SelectField } from "../../forms/inputGroup/helpers";
import { validateHotelSearch } from "./helpers";
import { useHistory } from "react-router-dom";
import {
  accSearchSystemInit,
  default_guest_nationality,
  startSearch,
} from "../../../actions/single_search/accommodation_search";
import jssVariables from "../../../jss_setup";
import { PaxPickerACC } from "../widgets/PaxPickerACC";
import { accSearchFormUpdate } from "../../../actions/search/accommodation_search";
import { useApoAuth, useHotelTypesSearch } from "../../../hooks";
import { SearchButton } from "../../common/Button";

const styles = createUseStyles({
  hotelSearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: !props?.hasNationality ? "repeat(4, max-content)" : "repeat(3, max-content)",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ...(props?.customStyles?.hotelSearchForm ?? {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    ...(props?.customStyles?.searchBtnContr ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    hotelSearchForm: (props) => ({
      gridTemplateRows: "unset",
      gridTemplateColumns: !props?.hasNationality
        ? "repeat(4, 1fr) max-content"
        : "repeat(3, 1fr) max-content",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      padding: 0,
      ...(props?.customStyles?.hotelSearchForm?.smallscreen ?? {}),
    }),
    searchBtnContr: (props) => ({
      padding: 0,
      justifyContent: "end",
      ...(props?.customStyles?.searchBtnContr?.smallscreen ?? {}),
    }),
  },
});
export function HotelSearchForm({ setShowPaxPickerModal, customStyles }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasNationality = !!default_guest_nationality;
  const classes = styles({ customStyles, hasNationality });

  const { t } = useContext(TranslationContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const { source_entity, market_type, website_api_key } = useApoAuth();

  const { types } = useHotelTypesSearch();

  const search_form = useSelector((state) => state.HotelSearchForm);

  const { checkIn, checkOut } = search_form;

  async function onFormSearch() {
    await dispatch(
      startSearch({
        redirect: true,
        history,
        source_entity,
        market_type,
        website_api_key,
      }),
    );
  }

  const onFormUpdate = useCallback(
    ({ form }) => {
      dispatch(accSearchFormUpdate({ form }));
    },
    [dispatch],
  );

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  const onResetSystem = useCallback(() => dispatch(accSearchSystemInit()), [dispatch]);
  // Reset accommodation search on mount.
  useEffect(() => {
    onResetSystem();
  }, []);

  return (
    <Formik
      initialValues={{
        destinationObj: {
          destination_code: search_form?.destination_code ?? [],
          destination_meta: search_form?.destination_meta ?? {},
        },
        datesArray: [checkIn, checkOut],
        nationality: "GR",
      }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);

          if (validateHotelSearch(values)) {
            const [checkIn, checkOut] = values.datesArray;
            await onFormUpdate({
              form: {
                checkIn,
                checkOut,
                destination_code: [...values.destinationObj.destination_code],
                destination_meta: { ...values.destinationObj.destination_meta },
                nationality: values.nationality,
              },
            });
            onFormSearch();
          } else {
            setIsSubmitting(false);
          }
        }
      }}
    >
      <Form className={classes.hotelSearchForm}>
        <DestinationsPicker
          id="destinationsAccPicker"
          name="destinationObj"
          types={types}
          product_type={"ACC"}
          placeholder={t("destinationPlaceholder")}
          customStyles={{
            ...(customStyles?.inputGroupField ?? {}),
            DestinationsPicker: customStyles?.DestinationsPicker ?? {},
          }}
          autocomplete={true}
          value={search_form?.destination_code ?? []}
        />
        <DatepickerField
          id="checkInOutAccDatepicker"
          label={t("checkInOut")}
          name="datesArray"
          selectsRange={true}
          dateFormat="MMM d, yyyy"
          minDate={DateTime.now().toISODate()}
          maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
          placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
          withPortal={true}
          customStyles={customStyles?.inputGroupField ?? {}}
        />
        <PaxPickerACC
          id="paxPickerAccBtn"
          customStyles={customStyles?.inputGroupField ?? {}}
          search_form={search_form}
          onClick={onClickPax}
        />
        {!default_guest_nationality && (
          <SelectField
            name="nationality"
            label="Nationality"
            options={Object.entries(countries).map(([code, name]) => [code, name])}
          />
        )}
        <div className={classes.searchBtnContr}>
          <SearchButton
            id="accSearchBtn"
            text={t("search")}
            loading={isSubmitting}
            customStyles={customStyles?.searchBtn ?? {}}
          />
        </div>
      </Form>
    </Formik>
  );
}
HotelSearchForm.defaultProps = { customStyles: {} };
HotelSearchForm.propTypes = {
  setLoadingSearchBtn: PropTypes.func.isRequired,
  setShowPaxPickerModal: PropTypes.func.isRequired,
  customStyles: PropTypes.object.isRequired,
};
